// Founder-assigned permanent Skill access.
//
// Reads `GET /api/brain/skill-access/agents` and
// `GET /api/brain/skill-access/agents/{id}`, writes through
// `POST /brain/skill-access/assignments` and `/{request_id}/revoke`.
//
// WHY, NOT JUST WHETHER
// =====================
// A Skill is reachable by an agent for one of three reasons, and they are not
// interchangeable:
//
//   department_pre_approval  the Skill's own department tags claim this agent's
//                            department. NOT revocable here — the way to change
//                            it is to change the tags on the Skill. The button
//                            is absent rather than disabled-with-an-error,
//                            because an affordance that always refuses is worse
//                            than no affordance.
//   founder_assignment       an explicit assignment made on this surface.
//   agent_request            the agent asked and the entitled approver in the
//                            chain said yes.
//
// The backend computes the reason (it owns `may_retrieve`); this component
// renders it. A second copy of that predicate in JavaScript would drift, and the
// drift would show as a surface claiming access an agent does not have.
//
// RETRIEVAL PERMISSIONS, NEVER GRANTS
// ===================================
// Nothing here issues a capability grant. A Skill is not grantable and the
// backend refuses any capability that is not a Skill, so this surface cannot
// become a route to authority over a callable Tool.
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { assignSkill, revokeSkillAssignment } from "../../../components/brain/lib/brainApi";
import { useBrainResource } from "../../../components/brain/lib/hooks";
import EmptyState from "../../components/EmptyState";
import { SectionTitle } from "../../components/PageHead";

export const REASON_LABEL = {
  department_pre_approval: "DEPARTMENT PRE-APPROVAL",
  founder_assignment: "FOUNDER ASSIGNMENT",
  agent_request: "REQUESTED & APPROVED",
  not_reachable: "NO ACCESS",
};

export const REASON_TONE = {
  department_pre_approval: "info",
  founder_assignment: "ok",
  agent_request: "ok",
  not_reachable: "unknown",
};

export function reasonLabel(reason) {
  return REASON_LABEL[reason] || "UNKNOWN";
}

/** Filters the skill rows. `access` is one of all | reachable | unreachable. */
export function filterSkills(skills, { access, query }) {
  const q = (query || "").trim().toLowerCase();
  return (skills || []).filter((s) => {
    if (access === "reachable" && !s.reachable) return false;
    if (access === "unreachable" && s.reachable) return false;
    if (!q) return true;
    return [s.name, s.capability_id, s.description, (s.departments || []).join(" ")]
      .join(" ").toLowerCase().includes(q);
  });
}

const ACCESS_TABS = [
  { id: "all", label: "All" },
  { id: "reachable", label: "Can reach" },
  { id: "unreachable", label: "No access" },
];

function agentName(a) {
  return `${a.title || a.agent_id}${a.department ? ` · ${a.department}` : ""}`;
}

function Row({ row, busy, onAssign, onRevoke }) {
  const perm = row.permission;
  return (
    <div className="sa-row" data-reachable={row.reachable ? "yes" : "no"}>
      <div className="sa-row-main">
        <b>{row.name}</b>
        {row.description ? <p>{row.description}</p> : null}
        <span className="sa-depts">
          {row.departments && row.departments.length
            ? `TAGGED: ${row.departments.join(", ")}`
            : "NO DEPARTMENT TAGS — reachable only by explicit assignment"}
        </span>
      </div>
      <div className="sa-row-side">
        <span className="sa-reason" data-tone={REASON_TONE[row.reason] || "unknown"}>
          {reasonLabel(row.reason)}
        </span>
        {/* Provenance of the explicit row, when there is one. Rendered even
            where a department tag currently masks it, because it is a real
            record and hiding it would make it unrevokable for no good reason. */}
        {perm ? (
          <span className="sa-by">
            by {perm.assigned_by || perm.decided_by || "—"}
            {perm.assigned_at || perm.decided_at
              ? ` · ${String(perm.assigned_at || perm.decided_at).slice(0, 10)}`
              : ""}
          </span>
        ) : null}
        {row.revocable && perm ? (
          <button className="sa-btn sa-revoke" disabled={busy}
                  onClick={() => onRevoke(row, perm)}>
            Revoke
          </button>
        ) : null}
        {!row.reachable ? (
          <button className="sa-btn sa-assign" disabled={busy}
                  onClick={() => onAssign(row)}>
            Assign
          </button>
        ) : null}
        {row.reason === "department_pre_approval" && !perm ? (
          <span className="sa-note-inline">
            From the Skill's tags. Not revocable here.
          </span>
        ) : null}
      </div>
    </div>
  );
}

export default function SkillAccess() {
  const agents = useBrainResource("/brain/skill-access/agents");
  const [agentId, setAgentId] = useState("");
  const [access, setAccess] = useState("all");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  const roster = useMemo(
    () => (agents.data && Array.isArray(agents.data.agents) ? agents.data.agents : []),
    [agents.data]
  );

  // Selecting the first agent is a convenience, not a decision: with none
  // selected the panel would render an empty shell that looks like a failure.
  useEffect(() => {
    if (!agentId && roster.length) setAgentId(roster[0].agent_id);
  }, [roster, agentId]);

  const detail = useBrainResource(
    agentId ? `/brain/skill-access/agents/${encodeURIComponent(agentId)}` : "/brain/skill-access/agents",
    { skip: !agentId }
  );
  const reload = detail.reload;

  const skills = useMemo(
    () => (detail.data && Array.isArray(detail.data.skills) ? detail.data.skills : []),
    [detail.data]
  );
  const shown = useMemo(() => filterSkills(skills, { access, query }), [skills, access, query]);

  const run = useCallback(async (fn, okMessage) => {
    setBusy(true);
    setFlash(null);
    const res = await fn();
    setBusy(false);
    if (res.ok) {
      setFlash({ tone: "ok", text: okMessage(res.data) });
      // Re-read rather than patching local state: the reason for a skill is
      // computed on the server, and guessing the new one here is how a surface
      // starts disagreeing with the thing it is describing.
      await reload();
    } else {
      setFlash({ tone: "crit", text: res.detail });
    }
  }, [reload]);

  const onAssign = useCallback((row) => run(
    () => assignSkill({ agentId, capabilityId: row.capability_id,
                        reason: "Founder-assigned from the Brain Skills & MCP surface" }),
    (d) => (d.already_held
      ? `${row.name} was already assigned — nothing changed.`
      : `${row.name} assigned. This is permanent until revoked.`)
  ), [agentId, run]);

  const onRevoke = useCallback((row, perm) => run(
    () => revokeSkillAssignment(perm.request_id,
                                { reason: "Revoked from the Brain Skills & MCP surface" }),
    () => `${row.name} revoked. The record is kept, marked revoked.`
  ), [run]);

  if (agents.status === "loading") {
    return <div className="skeleton" role="status" aria-label="Loading agents" />;
  }
  if (agents.status === "disabled") {
    return (
      <EmptyState
        title="Skill access is not enabled in this environment"
        detail="/api/brain/skill-access returns 404 while CIPHER_CAPABILITY_API_V1 is set to a falsey value. That is the flag's designed behaviour, not a failure."
      />
    );
  }
  if (agents.status === "error") {
    return (
      <EmptyState
        title="Could not read the agent roster"
        detail="The request failed. Nothing has been changed."
      />
    );
  }
  if (!roster.length) {
    return (
      <EmptyState
        title="No agents in the roster"
        detail="organization_agents is empty in this environment, so there is nobody to assign a Skill to."
      />
    );
  }

  const selected = roster.find((a) => a.agent_id === agentId);

  return (
    <div className="sa">
      <SectionTitle>Who can reach which Skill</SectionTitle>
      <p className="sa-lead">
        An assignment here is a <b>retrieval permission</b> and never a grant — a Skill is
        procedural knowledge, not a callable Tool, and nothing on this surface issues
        authority. Assignments do not expire; they last until revoked.
      </p>

      <div className="sa-bar">
        <label className="sa-pick">
          <span>Agent</span>
          <select value={agentId} aria-label="Agent"
                  onChange={(e) => { setAgentId(e.target.value); setFlash(null); }}>
            {roster.map((a) => (
              <option key={a.agent_id} value={a.agent_id}>{agentName(a)}</option>
            ))}
          </select>
        </label>
        <div className="sa-seg" role="tablist" aria-label="Filter by access">
          {ACCESS_TABS.map((t) => (
            <button key={t.id} role="tab" aria-selected={access === t.id}
                    className={access === t.id ? "on" : ""}
                    onClick={() => setAccess(t.id)}>{t.label}</button>
          ))}
        </div>
        <label className="sa-search">
          <input type="search" value={query} aria-label="Search skills"
                 placeholder="Search skills"
                 onChange={(e) => setQuery(e.target.value)} />
        </label>
      </div>

      {selected ? (
        <p className="sa-who">
          <b>{selected.title || selected.agent_id}</b> · {selected.layer || "—"} ·{" "}
          {selected.department || "no department"} · {selected.lifecycle || "—"}
          {detail.data ? (
            <> · reaches <b>{detail.data.reachable_count}</b> of {detail.data.count} Skills</>
          ) : null}
        </p>
      ) : null}

      {flash ? (
        <div className="sa-flash" data-tone={flash.tone} role="status">{flash.text}</div>
      ) : null}

      {detail.status === "loading" ? (
        <div className="skeleton" role="status" aria-label="Loading skill access" />
      ) : detail.status === "disabled" ? (
        <EmptyState
          title="Skill access is not enabled in this environment"
          detail="The route returned 404, which is what the flag does when it is off."
        />
      ) : detail.status === "error" ? (
        <EmptyState
          title="Could not read this agent's skill access"
          detail="The request failed. Nothing has been changed."
        />
      ) : !skills.length ? (
        <EmptyState
          title="No Skills registered"
          detail="The capability registry holds no records of kind `skill` in this environment."
        />
      ) : !shown.length ? (
        <EmptyState
          title="Nothing matches that filter"
          detail={`This agent's view holds ${skills.length} Skills; none of them answer this question.`}
        />
      ) : (
        <div className="sa-list">
          {shown.map((row) => (
            <Row key={row.capability_id} row={row} busy={busy}
                 onAssign={onAssign} onRevoke={onRevoke} />
          ))}
        </div>
      )}

      <p className="sa-foot">
        Department pre-approval comes from the Skill's own department tags and cannot be
        withdrawn here. Revoking marks the record revoked rather than deleting it, so who
        held what — and who took it away — survives.
      </p>
    </div>
  );
}
